import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import { useAuth } from "../../lib/auth";
import { useLiveScores } from "../../hooks/useLiveScores";
import { useCreateGame, useCurrentUserProfile, type CreateGameInput } from "../../lib/api";
import Layout from "../../components/Layout";

const OPTION_TYPE_KEYS: Record<string, string> = {
  winner: "games.optionTypeWinner",
  assists: "games.optionTypeAssists",
  yellow_cards: "games.optionTypeYellowCards",
  red_cards: "games.optionTypeRedCards",
  goals: "games.optionTypeGoals",
  custom: "games.optionTypeCustom",
};

const CreateGamePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile, isLoading: loadingProfile } = useCurrentUserProfile(!!user?.id);
  const { data: leagues, isLoading: loadingMatches } = useLiveScores();
  const createGame = useCreateGame();

  const isAdmin = profile?.admin === true;

  const [matchId, setMatchId] = useState<number>(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [coinCost, setCoinCost] = useState(1);
  const [minParticipants, setMinParticipants] = useState<number | "">("");
  const [gameStatus, setGameStatus] = useState<"draft" | "open">("open");
  const [options, setOptions] = useState<Array<{ type: string; label: string; order: number }>>(() => [
    { type: "winner", label: "", order: 0 },
    { type: "goals", label: "", order: 1 },
  ]);

  const matchOptions = useMemo(() => {
    if (!leagues) return [];
    const list: Array<{ matchId: number; label: string }> = [];
    for (const league of leagues) {
      for (const d of league.dates || []) {
        for (const m of d.matches || []) {
          const dbId = (m as { matchId?: number }).matchId;
          if (dbId == null) continue; // need DB primary key for game creation
          const hostName = m.host?.name ?? "?";
          const guestName = m.guest?.name ?? "?";
          list.push({ matchId: dbId, label: `${hostName} vs ${guestName} · ${m.date ?? ""}` });
        }
      }
    }
    return list;
  }, [leagues]);

  const addOption = () => {
    setOptions((prev) => [...prev, { type: "custom", label: "", order: prev.length }]);
  };

  const updateOption = (index: number, field: "type" | "label" | "order", value: string | number) => {
    setOptions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === "order") next[index].order = Number(value);
      return next;
    });
  };

  const removeOption = (index: number) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!matchId || !title.trim() || options.length === 0 || coinCost < 1) return;
    const opts = options.map((o, i) => ({ type: o.type, label: o.label.trim() || o.type, order: i }));
    const payload: CreateGameInput = {
      matchId,
      title: title.trim(),
      description: description.trim(),
      coinCost: Number(coinCost) || 1,
      minParticipants: minParticipants === "" ? undefined : Number(minParticipants),
      status: gameStatus,
      options: opts,
    };
    createGame.mutate(payload, {
      onSuccess: (data: any) => {
        const id = data?.id ?? data?.ID;
        if (id) navigate(`/games/${id}`);
        else navigate("/games");
      },
    });
  };

  if (!user?.id) {
    return (
      <Layout title={t("games.title")}>
        <div className="min-h-screen bg-[#0f0f12] flex items-center justify-center px-4">
          <p className="text-amber-200/80">
            {t("games.signInToCreateGame")}
          </p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="me-2 text-amber-400 underline"
          >
            {t("auth.login")}
          </button>
        </div>
      </Layout>
    );
  }

  if (loadingProfile) {
    return (
      <Layout title={t("games.title")}>
        <div className="min-h-screen bg-[#0f0f12] flex items-center justify-center px-4 text-gray-400">
          {t("common.loading")}
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return (
      <Layout title={t("games.title")}>
        <div className="min-h-screen bg-[#0f0f12] flex flex-col items-center justify-center px-4">
          <p className="text-amber-200/80 text-center mb-4">
            {t("games.adminOnly")}
          </p>
          <button
            type="button"
            onClick={() => navigate("/games")}
            className="text-amber-400 underline"
          >
            {t("games.backToGames")}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t("games.title")}>
      <div className="min-h-screen bg-[#0f0f12] text-gray-100 px-4 pb-12">
        <div className="mb-4 pt-4">
          <button
            type="button"
            onClick={() => navigate("/games")}
            className="flex items-center text-amber-200/80 hover:text-amber-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 me-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            {t("games.back")}
          </button>
        </div>

        <h1 className="text-xl font-bold text-amber-200 mb-2">{t("games.createGameTitle")}</h1>
        <p className="text-gray-400 text-sm mb-6">{t("games.createGameSubtitle")}</p>

        {loadingMatches && <p className="text-gray-400 text-sm mb-4">{t("games.loadingMatches")}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">{t("games.matchLabel")}</label>
            <select
              value={matchId || ""}
              onChange={(e) => setMatchId(Number(e.target.value))}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-gray-100"
              required
            >
              <option value="">{t("games.selectMatch")}</option>
              {matchOptions.map((opt) => (
                <option key={opt.matchId} value={opt.matchId}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">{t("games.titleLabel")}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-gray-100 placeholder-gray-500"
              placeholder={t("games.titlePlaceholder")}
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">{t("games.descriptionLabel")}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-gray-100 placeholder-gray-500"
              placeholder={t("games.descriptionPlaceholder")}
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">{t("games.coinsToJoinLabel")}</label>
            <input
              type="number"
              min={1}
              required
              value={coinCost}
              onChange={(e) => setCoinCost(Math.max(1, Number(e.target.value) || 1))}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-gray-100"
            />
            <p className="text-gray-500 text-xs mt-1.5">{t("games.coinsToJoinHint")}</p>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">{t("games.minParticipantsLabel")}</label>
            <input
              type="number"
              min={0}
              value={minParticipants}
              onChange={(e) => setMinParticipants(e.target.value === "" ? "" : Number(e.target.value))}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">{t("games.createGameInitialStatusLabel")}</label>
            <select
              value={gameStatus}
              onChange={(e) => setGameStatus(e.target.value as "draft" | "open")}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-gray-100"
            >
              <option value="open">{t("games.open")}</option>
              <option value="draft">{t("games.draft")}</option>
            </select>
            <p className="text-gray-500 text-xs mt-1.5 leading-relaxed">{t("games.createGameStatusHint")}</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm text-gray-400">{t("games.predictionOptionsLabel")}</label>
              <button type="button" onClick={addOption} className="text-sm text-amber-400 hover:text-amber-300">
                {t("games.addOption")}
              </button>
            </div>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <div key={i} className="flex flex-wrap gap-2 items-center rounded-lg bg-white/5 border border-white/10 p-2">
                  <select
                    value={opt.type}
                    onChange={(e) => updateOption(i, "type", e.target.value)}
                    className="rounded bg-white/5 border border-white/10 px-2 py-1 text-gray-100 text-sm"
                  >
                    {Object.entries(OPTION_TYPE_KEYS).map(([value, key]) => (
                      <option key={value} value={value}>{t(key)}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={opt.label}
                    onChange={(e) => updateOption(i, "label", e.target.value)}
                    placeholder={t("games.optionLabelPlaceholder")}
                    className="flex-1 min-w-[120px] rounded bg-white/5 border border-white/10 px-2 py-1 text-gray-100 text-sm placeholder-gray-500"
                  />
                  <span className="text-gray-500 text-sm">{t("games.orderLabelWithNumber", { n: String(i) })}</span>
                  <button type="button" onClick={() => removeOption(i)} className="text-red-400 hover:text-red-300 text-sm">
                    {t("games.removeOption")}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {createGame.isError && (
            <p className="text-amber-400 text-sm">{createGame.error?.message}</p>
          )}

          <button
            type="submit"
            disabled={createGame.isPending || !matchId || !title.trim() || options.length === 0 || coinCost < 1}
            className="w-full py-3 rounded-xl bg-amber-500 text-black font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createGame.isPending ? t("common.loading") : t("games.createGameButton")}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default CreateGamePage;
